import { useEffect, useRef, useCallback } from "react"
import { usePage } from "@inertiajs/react"
import type { CurrentUser } from "@/types/shared"

type OnlineStatus = "online" | "away" | "offline"

interface PageProps {
  current_user: CurrentUser | null
}

// Aligned with PresenceConfig::Client::HEARTBEAT_INTERVAL (60 seconds)
const HEARTBEAT_INTERVAL = 60 * 1000 // 1 minute
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"]

/**
 * Hook that tracks user activity and sends heartbeats to maintain online status.
 * Also handles auto-away detection and status updates.
 */
export function useActivityTracker(onStatusChange?: (status: OnlineStatus) => void) {
  const { props: pageProps } = usePage<{ props: PageProps }>()
  const { current_user } = pageProps as unknown as PageProps

  const lastActivityRef = useRef<number>(Date.now())
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const currentStatusRef = useRef<OnlineStatus>((current_user?.online_status as OnlineStatus) || "offline")

  const getCsrfToken = useCallback(() => {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
  }, [])

  const sendHeartbeat = useCallback(async () => {
    if (!current_user || currentStatusRef.current === "offline") return

    try {
      const response = await fetch("/profile/heartbeat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.status !== currentStatusRef.current) {
          currentStatusRef.current = data.status
          onStatusChange?.(data.status)
        }
      }
    } catch (error) {
      console.error("Heartbeat failed:", error)
    }
  }, [current_user, getCsrfToken, onStatusChange])

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
  }, [])

  useEffect(() => {
    if (!current_user) return

    // Set up activity event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Set up heartbeat interval
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)

    // Send initial heartbeat
    sendHeartbeat()

    return () => {
      // Clean up event listeners
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })

      // Clear heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
    }
  }, [current_user, handleActivity, sendHeartbeat])

  // Handle visibility change - send heartbeat when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        sendHeartbeat()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [sendHeartbeat])

  return {
    lastActivity: lastActivityRef.current,
    sendHeartbeat,
  }
}
